import React, { useEffect, useState } from "react";
import { httpGet, httpPut, httpPost, httpPatch } from "../../components/helpers/httpMethods";
import { Settings } from "./AccountMore";

// Account settings with tabs: Your details (existing form), About you (birthday, work, place, topics),
// Emails (what TNS may send) and the Publisher profile card (publishers only). Saves to /api/me/profile.
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const TOPICS = ["Business", "Politics", "Tech", "Sports", "Entertainment", "Health", "Education", "Faith", "Lifestyle", "Opinion"];
const INDUSTRIES = ["Banking and finance", "Technology", "Media and publishing", "Government and public service", "Oil, gas and energy", "Telecoms", "Manufacturing",
  "Agriculture", "Education", "Health", "Legal", "Real estate and construction", "Retail and trade", "Transport and logistics", "Faith organisation", "NGO", "Student", "Self-employed", "Other"];
const STATES = ["Abia", "Adamawa", "Akwa Ibom", "Anambra", "Bauchi", "Bayelsa", "Benue", "Borno", "Cross River", "Delta", "Ebonyi", "Edo", "Ekiti", "Enugu", "FCT Abuja", "Gombe", "Imo",
  "Jigawa", "Kaduna", "Kano", "Katsina", "Kebbi", "Kogi", "Kwara", "Lagos", "Nasarawa", "Niger", "Ogun", "Ondo", "Osun", "Oyo", "Plateau", "Rivers", "Sokoto", "Taraba", "Yobe", "Zamfara", "Outside Nigeria"];
const errText = (r, f) => (r && r.er && (r.er.message || (typeof r.er === "string" ? r.er : ""))) || f;

function Progress({ c }) {
  if (!c) return null;
  const nextLabel = { birthday: "your birthday treat", work: "company news alerts", topics: "papers picked for you", phone: "account security", name: "a personal account" }[c.next];
  return (
    <div className="ac-card pf-progress">
      <span className="pf-ring" style={{ background: `conic-gradient(#b45309 0 ${c.percent}%, #fdebd7 ${c.percent}% 100%)` }}><span>{c.percent}%</span></span>
      <span className="pf-progress-text">
        <strong>{c.percent === 100 ? "Your profile is complete" : `Your profile is ${c.percent}% complete`}</strong>
        <small>{c.percent === 100 ? "Thank you. Your birthday treat, alerts and picks are all set." : `Next: unlock ${nextLabel}.`}</small>
        <span className="pf-chips">{c.items.map((i) => <span key={i.key} className={i.done ? "on" : ""}>{i.done ? "✓ " : ""}{i.label}</span>)}</span>
      </span>
    </div>
  );
}
const Why = ({ children }) => <p className="pf-why"><span aria-hidden="true">🎁</span>{children}</p>;

export function AboutYou({ data, onSaved }) {
  const p = (data && data.profile) || {};
  const [f, setF] = useState({ birthDay: p.birthDay || "", birthMonth: p.birthMonth || "", birthYear: p.birthYear || "", company: p.company || "", jobTitle: p.jobTitle || "",
    industry: p.industry || "", state: p.state || "", city: p.city || "", topics: Array.isArray(p.topics) ? p.topics : [] });
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(null);
  const set = (k) => (e) => { setF({ ...f, [k]: e.target.value }); setNote(null); };
  const toggleTopic = (t) => { setF({ ...f, topics: f.topics.includes(t) ? f.topics.filter((x) => x !== t) : [...f.topics, t] }); setNote(null); };
  const save = async () => {
    if ((f.birthDay && !f.birthMonth) || (!f.birthDay && f.birthMonth)) return setNote({ ok: false, text: "Please choose both the day and the month of your birthday." });
    setBusy(true);
    const r = await httpPut("me/profile", { ...f, birthDay: f.birthDay || null, birthMonth: f.birthMonth || null, birthYear: f.birthYear || null });
    setBusy(false);
    if (!r || r.er || !r.data) return setNote({ ok: false, text: errText(r, "We couldn’t save. Please try again.") });
    setNote({ ok: true, text: "Saved. Thank you!" });
    onSaved(r.data);
  };
  const days = Array.from({ length: 31 }, (_, i) => i + 1);
  return (
    <section className="ac-card ac-form">
      <div><h2 className="ac-form-title">About you</h2><p className="ac-muted">Get birthday treats, alerts when your company makes the news, and papers picked for you. Everything is optional.</p></div>
      <div className="pf-group">
        <h3>🎂 Your birthday</h3>
        <div className="pf-grid3">
          <label className="ac-field"><span>Day</span><select className="ac-input" value={f.birthDay} onChange={set("birthDay")}><option value="">Day</option>{days.map((d) => <option key={d} value={d}>{d}</option>)}</select></label>
          <label className="ac-field"><span>Month</span><select className="ac-input" value={f.birthMonth} onChange={set("birthMonth")}><option value="">Month</option>{MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}</select></label>
          <label className="ac-field"><span>Year <em>(optional)</em></span><input className="ac-input" inputMode="numeric" maxLength={4} placeholder="e.g. 1990" value={f.birthYear} onChange={set("birthYear")} /></label>
        </div>
        <Why>A birthday treat every year, plus the front pages from your birthdays since 2020.</Why>
      </div>
      <div className="pf-group">
        <h3>💼 Your work</h3>
        <div className="pf-grid3">
          <label className="ac-field"><span>Company</span><input className="ac-input" placeholder="e.g. Tilka Technologies" value={f.company} onChange={set("company")} maxLength={120} /></label>
          <label className="ac-field"><span>Job title</span><input className="ac-input" placeholder="e.g. Product Manager" value={f.jobTitle} onChange={set("jobTitle")} maxLength={120} /></label>
          <label className="ac-field"><span>Industry</span><select className="ac-input" value={f.industry} onChange={set("industry")}><option value="">Choose…</option>{INDUSTRIES.map((x) => <option key={x}>{x}</option>)}</select></label>
        </div>
        <Why>Get an email when your company is mentioned in any newspaper on TNS.</Why>
      </div>
      <div className="pf-group">
        <h3>📍 Where you are</h3>
        <div className="pf-grid3">
          <label className="ac-field"><span>State</span><select className="ac-input" value={f.state} onChange={set("state")}><option value="">Choose…</option>{STATES.map((x) => <option key={x}>{x}</option>)}</select></label>
          <label className="ac-field"><span>City or town</span><input className="ac-input" placeholder="e.g. Ikeja" value={f.city} onChange={set("city")} maxLength={120} /></label>
        </div>
        <Why>We’ll show regional papers and local notices first.</Why>
      </div>
      <div className="pf-group">
        <h3>📰 Topics you follow</h3>
        <div className="pf-topics">{TOPICS.map((t) => <button type="button" key={t} className={f.topics.includes(t) ? "on" : ""} aria-pressed={f.topics.includes(t)} onClick={() => toggleTopic(t)}>{t}</button>)}</div>
        <Why>A “For you” row on your home page.</Why>
      </div>
      <div className="ac-form-foot pf-foot">
        <small className="ac-muted">🔒 We never sell your details. Edit or delete them any time.</small>
        <span className="ac-grow" />
        {note ? <p className={`ac-note ${note.ok ? "ok" : "err"}`} role={note.ok ? "status" : "alert"}>{note.text}</p> : null}
        <button type="button" className="ac-btn" onClick={save} disabled={busy}>{busy ? "Saving…" : "Save"}</button>
      </div>
    </section>
  );
}

export function Emails({ data, onSaved }) {
  const p = (data && data.profile) || {};
  const [v, setV] = useState({ emailFollowing: p.emailFollowing !== false, emailBirthday: p.emailBirthday !== false, emailCompany: p.emailCompany !== false, emailPicks: !!p.emailPicks, emailOffers: data && typeof data.emailOffersOn === "boolean" ? data.emailOffersOn : !!p.emailOffers });
  const [note, setNote] = useState(null);
  const flip = async (k) => {
    const next = { ...v, [k]: !v[k] };
    setV(next); setNote(null);
    const r = await httpPut("me/profile", { [k]: next[k] });
    if (!r || r.er) { setV(v); return setNote({ ok: false, text: errText(r, "We couldn’t save that. Please try again.") }); }
    setNote({ ok: true, text: "Saved." }); onSaved(r.data);
  };
  const rows = [["emailFollowing", "Your week in papers", "Sundays, only when papers you follow have new editions"], ["emailBirthday", "Birthday treat", "Once a year, on your birthday"], ["emailCompany", "Company in the news", "When your company appears in a paper (add your company in About you)"],
    ["emailPicks", "Picks for you", "A short weekly email of editions you may like"], ["emailOffers", "News and offers from TheNewspaperStand", "Tips to get started, new papers and old issues. Never more than one a week"]];
  return (
    <section className="ac-card ac-form">
      <div><h2 className="ac-form-title">Emails from TheNewspaperStand</h2><p className="ac-muted">Choose what we send. Account emails (receipts, renewals, applications) always arrive.</p></div>
      <div>{rows.map(([k, t, s]) => (
        <button type="button" key={k} className="pf-switch-row" role="switch" aria-checked={v[k]} onClick={() => flip(k)}>
          <span className={`pf-switch${v[k] ? " on" : ""}`}><span /></span>
          <span className="pf-switch-text"><strong>{t}</strong><small>{s}</small></span>
        </button>
      ))}</div>
      {note ? <p className={`ac-note ${note.ok ? "ok" : "err"}`} role={note.ok ? "status" : "alert"}>{note.text}</p> : null}
    </section>
  );
}

export function PublisherCard({ userType }) {
  const [p, setP] = useState(null);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(null);
  useEffect(() => {
    httpGet("auth/profile_details").then((r) => {
      const d = r && r.data; const u = d ? [d.user, d.profile, d.userDetails, d.data, d].find((x) => x && x.email) : null;
      if (u) { setP(u); setF({ username: u.username || "", bio: u.bio || "", instagramHandle: u.instagramHandle || "", fbHandle: u.fbHandle || "", twitterHandle: u.twitterHandle || "", youtubeHandle: u.youtubeHandle || "" }); }
    });
  }, []);
  if (!p) return null;
  const isPublisher = (userType && !/^(user|reader|customer|subscriber)$/i.test(String(userType))) || ["username", "bio", "instagramHandle", "fbHandle", "twitterHandle", "youtubeHandle"].some((k) => p[k]);
  if (!isPublisher) return null;
  const set = (k) => (e) => { setF({ ...f, [k]: e.target.value }); setNote(null); };
  const save = async () => {
    setBusy(true);
    const r = await httpPatch("auth/edit_profile", { bio: f.bio, country: p.country, email: p.email, fbHandle: f.fbHandle, firstName: p.firstName, instagramHandle: f.instagramHandle,
      countryCode: p.countryCode, phoneNumber: p.phoneNumber, jobPosition: p.jobPosition, lastName: p.lastName, twitterHandle: f.twitterHandle, username: f.username, youtubeHandle: f.youtubeHandle });
    setBusy(false);
    if (!r || r.er) return setNote({ ok: false, text: errText(r, "We couldn’t save. Please try again.") });
    setNote({ ok: true, text: "Publisher profile saved." });
  };
  return (
    <section className="ac-card ac-form ac-quiet">
      <div className="pf-pub-head"><span aria-hidden="true">🏢</span><span><h2 className="ac-form-title">Publisher profile</h2><small className="ac-muted">Your public username, tagline and social links.</small></span>
        <span className="ac-grow" /><button type="button" className="ac-link" onClick={() => setOpen(!open)}>{open ? "Close" : "Edit ›"}</button></div>
      {open ? (
        <>
          <div className="pf-grid3">
            <label className="ac-field"><span>Username</span><input className="ac-input" value={f.username} onChange={set("username")} /></label>
            <label className="ac-field"><span>Instagram</span><input className="ac-input" value={f.instagramHandle} onChange={set("instagramHandle")} /></label>
            <label className="ac-field"><span>Facebook</span><input className="ac-input" value={f.fbHandle} onChange={set("fbHandle")} /></label>
            <label className="ac-field"><span>X (Twitter)</span><input className="ac-input" value={f.twitterHandle} onChange={set("twitterHandle")} /></label>
            <label className="ac-field"><span>YouTube</span><input className="ac-input" value={f.youtubeHandle} onChange={set("youtubeHandle")} /></label>
          </div>
          <label className="ac-field"><span>Tagline <em>(up to 1,000 characters)</em></span><textarea className="ac-input pf-area" maxLength={1000} value={f.bio} onChange={set("bio")} /></label>
          <div className="ac-form-foot"><button type="button" className="ac-btn" onClick={save} disabled={busy}>{busy ? "Saving…" : "Save publisher profile"}</button>{note ? <p className={`ac-note ${note.ok ? "ok" : "err"}`}>{note.text}</p> : null}</div>
        </>
      ) : null}
    </section>
  );
}

export function SettingsHub({ history }) {
  const tabFrom = () => { const t = new URLSearchParams(window.location.search).get("tab"); return ["details", "about", "emails"].includes(t) ? t : "details"; };
  const [tab, setTab] = useState(tabFrom);
  const [data, setData] = useState(null);
  useEffect(() => { httpGet("me/profile").then((r) => setData((r && r.data) || { profile: null, completeness: null })); }, []);
  const go = (t) => { setTab(t); if (history) history.replace(`/account/settings${t === "details" ? "" : `?tab=${t}`}`); };
  return (
    <>
      <div className="ac-title"><h1>Account settings</h1><p>Your details, what you tell us about yourself, and your emails.</p></div>
      <div className="ac-tabs pf-tabs" role="tablist">
        {[["details", "Your details"], ["about", "About you"], ["emails", "Emails"]].map(([k, l]) => (
          <button type="button" key={k} role="tab" aria-selected={tab === k} className={tab === k ? "on" : ""} onClick={() => go(k)}>
            {l}{k === "about" && data && data.completeness && data.completeness.percent < 100 ? <i className="pf-dot" aria-label="incomplete" /> : null}
          </button>
        ))}
      </div>
      {tab === "details" ? <Settings /> : !data ? <p className="ac-muted">Loading…</p> : tab === "about" ? (
        <><Progress c={data.completeness} /><AboutYou data={data} onSaved={(d) => setData({ ...data, ...d })} /><PublisherCard userType={data.userType} /></>
      ) : <Emails data={data} onSaved={(d) => setData({ ...data, ...d })} />}
    </>
  );
}

// One gentle question at a time on the Overview (hidden for 30 days with "Not now")
export function ProfileNudge({ history }) {
  const [c, setC] = useState(null);
  const [day, setDay] = useState("");
  const [month, setMonth] = useState("");
  const [busy, setBusy] = useState(false);
  const [gone, setGone] = useState(false);
  const [thanks, setThanks] = useState(false);
  useEffect(() => { httpGet("me/profile").then((r) => setC(r && r.data && r.data.completeness)); }, []);
  if (!c || gone || (!c.prompt && !thanks)) return null;
  if (thanks) return <div className="ac-card pf-nudge"><span className="pf-nudge-icon" aria-hidden="true">🎉</span><span className="pf-nudge-text"><strong>Saved, thank you!</strong><small>We’ll send your birthday treat on the day.</small></span></div>;
  const later = () => { setGone(true); httpPost("me/profile/snooze", { key: c.prompt }); };
  const ring = <span className="pf-ring sm" style={{ background: `conic-gradient(#b45309 0 ${c.percent}%, #fdebd7 ${c.percent}% 100%)` }}><span>{c.percent}%</span></span>;
  if (c.prompt === "birthday") {
    const save = async () => {
      if (!day || !month) return;
      setBusy(true);
      const r = await httpPut("me/profile", { birthDay: day, birthMonth: month });
      setBusy(false);
      if (r && !r.er) setThanks(true);
    };
    return (
      <div className="ac-card pf-nudge">
        {ring}
        <span className="pf-nudge-text"><strong>When’s your birthday? 🎂</strong><small>Tell us and get a treat from TNS on your birthday. Just the day and month.</small></span>
        <span className="pf-nudge-form">
          <select className="ac-input" value={day} onChange={(e) => setDay(e.target.value)} aria-label="Day"><option value="">Day</option>{Array.from({ length: 31 }, (_, i) => <option key={i + 1} value={i + 1}>{i + 1}</option>)}</select>
          <select className="ac-input" value={month} onChange={(e) => setMonth(e.target.value)} aria-label="Month"><option value="">Month</option>{MONTHS.map((m, i) => <option key={m} value={i + 1}>{m.slice(0, 3)}</option>)}</select>
          <button type="button" className="ac-btn" onClick={save} disabled={busy || !day || !month}>{busy ? "…" : "Save"}</button>
        </span>
        <button type="button" className="ac-link pf-later" onClick={later}>Not now</button>
      </div>
    );
  }
  const text = { work: ["Is your company in the news? 💼", "Add where you work and we’ll alert you when it’s mentioned in any paper."], topics: ["Pick your topics 📰", "Tell us what you like to read and we’ll pick papers for you."],
    phone: ["Add your phone number", "So we can reach you about your account if we need to."], name: ["Add your name", "So we know what to call you."] }[c.prompt] || ["Complete your profile", ""];
  return (
    <div className="ac-card pf-nudge">
      {ring}
      <span className="pf-nudge-text"><strong>{text[0]}</strong><small>{text[1]}</small></span>
      <button type="button" className="ac-btn" onClick={() => history.push(c.prompt === "phone" || c.prompt === "name" ? "/account/settings" : "/account/settings?tab=about")}>Add it</button>
      <button type="button" className="ac-link pf-later" onClick={later}>Not now</button>
    </div>
  );
}

// Overview: "Your company in the news" (only when the reader has added a company)
export function CompanyNews({ history }) {
  const [d, setD] = useState(null);
  useEffect(() => { httpGet("me/mentions").then((r) => setD((r && r.data) || null)); }, []);
  if (!d || !d.company) return null;
  const day = (x) => new Date(x).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", timeZone: "Africa/Lagos" });
  const mark = (t) => { const s = String(t || "").replace(/\s+/g, " ").trim(); const i = s.toLowerCase().indexOf(d.company.toLowerCase());
    return i < 0 ? s : <>{s.slice(0, i)}<mark>{s.slice(i, i + d.company.length)}</mark>{s.slice(i + d.company.length)}</>; };
  return (
    <section className="ac-card">
      <div className="ac-head"><h2>“{d.company}” in the news</h2><button type="button" className="ac-link" onClick={() => history.push("/account/settings?tab=about")}>Change ›</button></div>
      {!d.mentions.length ? <p className="ac-muted">No mentions in the last 14 days. We’ll email you when it appears in any paper.</p> : d.mentions.map((x) => (
        <button type="button" key={x.editionId} className="pf-mention" onClick={() => history.push(`/view_single_paper/${x.editionId}`)}>
          <span className="ac-cover">{x.image ? <img src={x.image} alt="" loading="lazy" /> : null}</span>
          <span className="pf-mention-text"><strong>{x.paper} · page {x.page}</strong><small>{day(x.publishedDate)}</small><span>…{mark(x.snippet)}…</span></span>
          <em>Read ›</em>
        </button>
      ))}
    </section>
  );
}

// Overview: birthday treat (today's papers free) and "Your birthdays in the news"
export function BirthdayPanel({ history, user }) {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  useEffect(() => { httpGet("me/birthday").then((r) => setD((r && r.data) || null)); }, []);
  if (!d || !d.set) return null;
  const long = (x) => new Date(x).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric", timeZone: "Africa/Lagos" });
  const claim = async (e) => {
    if ((d.claimed || []).includes(e.id)) { history.push(`/view_single_paper/${e.id}`); return; }
    setBusy(e.id); setErr("");
    const r = await httpPost("me/birthday/claim", { editionId: e.id });
    setBusy("");
    if (!r || r.er) { setErr(errText(r, "We couldn’t open that paper. Please try again.")); return; }
    history.push(`/view_single_paper/${e.id}`);
  };
  const past = d.past || [];
  if (!d.isBirthday && !past.length) return null;
  return (
    <section className={`ac-card bd-card${d.isBirthday ? " today" : ""}`}>
      {d.isBirthday ? (
        <>
          <div className="bd-hero"><span aria-hidden="true">🎂</span><span><strong>Happy birthday{user && user.firstName ? `, ${user.firstName}` : ""}!</strong>
            <small>Today, any of today’s papers is on us. Tap Read free; they stay in your library. ({(d.claimed || []).length} of {d.limit} used)</small></span></div>
          {err ? <p className="ac-note err">{err}</p> : null}
          <div className="bd-grid">
            {(d.today || []).map((e) => (
              <button type="button" key={e.id} className="bd-ed" onClick={() => claim(e)} disabled={busy === e.id}>
                <span className="ac-cover">{e.image ? <img src={e.image} alt="" loading="lazy" /> : null}</span>
                <strong>{e.paper}</strong>
                <em>{(d.claimed || []).includes(e.id) ? "Open ›" : busy === e.id ? "Opening…" : "Read free"}</em>
              </button>
            ))}
          </div>
        </>
      ) : null}
      {past.length ? (
        <>
          <div className="ac-head bd-past-head"><h2>Your birthdays in the news</h2><small className="ac-muted">Front pages from your birthdays since 2020</small></div>
          <div className="bd-past">
            {past.map((e) => (
              <button type="button" key={e.id} className="bd-ed" onClick={() => history.push(`/view_single_paper/${e.id}`)}>
                <span className="ac-cover">{e.image ? <img src={e.image} alt="" loading="lazy" /> : null}</span>
                <strong>{e.year}</strong><small className="bd-paper" title={e.paper}>{e.paper}</small><small>{long(e.publishedDate)}</small>
              </button>
            ))}
          </div>
        </>
      ) : null}
    </section>
  );
}
